import gql from 'graphql-tag';
export const ENVIRONMENT_SETTINGS_FIELDS = gql`
    fragment environmentSettingsFragment on EnvironmentSettings3JS {
        envMapSettings {
            url
            height
            radius
        }
        lightsSettings {
            ... on DirectionalLight3JS {
                color
                intensity
                shadowSettings {
                    mapSize
                    bias
                    cameraLeft
                    cameraRight
                    cameraTop
                    cameraBottom
                }
                position {
                    x
                    y
                    z
                }
                target {
                    x
                    y
                    z
                }
            }
            ... on HemisphereLight3JS {
                skyColor
                groundColor
                intensity
                position {
                    x
                    y
                    z
                }
            }
        }
        shadowFloorOpacity
    }
`;

export const RETAILER_FIELDS = gql`
    ${ENVIRONMENT_SETTINGS_FIELDS}
    fragment assetFragment on Asset {
        id
        filename
        mimeType
        directory
        url
    }
    fragment retailerFragment on Retailer {
        id
        name
        email
        location
        priceStocks {
            edges {
                node {
                    id
                    sku
                    data {
                        ... on ItemPriceStock {
                            stock
                            price
                            EAN
                            internalCode
                            purchasePrice
                            discountPrice
                        }
                        ... on WoodPriceStock {
                            prices {
                                values {
                                    key
                                    value {
                                        stock
                                        price
                                        EAN
                                        internalCode
                                        purchasePrice
                                        discountPrice
                                    }
                                }
                            }
                        }
                    }
                    scope
                }
            }
            pageInfo{
                hasNextPage
            }
            count
        }
        retailerOptions {
            currencyOptions {
                currency
                showCurrency
                locale
                showThousandsSeparator
            }
            colorCodes {
                button
                buttonPrimaryHover
                buttonSecondaryHover
                gradient1
                gradient2
                cartButton
                cartButtonHover
                background
                selectedItemBackground
                bodyText
                headerText
                selectedItem
                icons
                activeStep
                inactiveStep
                dropShadow
                warn
                border
            }
            agreementLinks {
                href
                text
            }
            helpLink {
                href
                text
            }
            woodTypesAvailable {
                woodType
                available
            }
            configurationIdentifier
            resultSceneSkyMaterialReference
            resultSceneFloorMaterialReference
            purchasePriceMandatory
            installationSKUs {
                values {
                    key
                    value
                }
            }
            preconfigurations
            helpItems {
                order
                title
                videoAsset {
                    ...assetFragment
                }
                thumbnailAsset {
                    ...assetFragment
                }
            }
            webhookOptions {
                flowType
                webhookUrls {
                    redirectUrl
                }
            }
            codeParameterName
            configuratorOptions {
                favicon {
                    ...assetFragment
                }
                banner {
                    ...assetFragment
                }
                font {
                    regularFontAsset {
                        ...assetFragment
                    }
                    boldFontAsset {
                        ...assetFragment
                    }
                    italicFontAsset {
                        ...assetFragment
                    }
                    mediumFontAsset {
                        ...assetFragment
                    }
                }
                configuratorIcons {
                    measurement {
                        ...assetFragment
                    }
                    woodType {
                        ...assetFragment
                    }
                    reset {
                        ...assetFragment
                    }
                }
                priceAppearance {
                    font {
                        ...assetFragment
                    }
                    color
                }
                bottomLogo
                saveButton
                hideSteps
                hideMenuSaveButton
                hideMenuOpenButton
                hideShareContent
                extraCardWidth,
                discountedPriceAppearance {
                    strikeThrough
                    textSize
                    color
                }
                hoverOverEffectEnabled
                environmentSettingsEditView {
                    ...environmentSettingsFragment
                }
                environmentSettingsResultView {
                    ...environmentSettingsFragment
                }
                saveWithMail
            }
            eanOptions {
                eanEnabled
            }
            hornbachOptions {
                integrationServiceUrl
                articleId
                companyCode
                locale
            }
        }
        safetyEnvironment {
            invariants {
                rule
                correction
                name
                id
                translations {
                    values {
                        key
                        value
                    }
                }
                translationKeys {
                    key
                    value
                }
                disableDuringOnChange
            }
            invariantTemplates
        }
        isDeleted
        rank
    }
`;

export const MODULE_FIELDS = gql`
    fragment attributeFragment on Attribute {
        id
        name
        attributeType
        title
    }
`;

export const BLUEPRINT_FIELDS = gql`
    fragment blueprintFragment on ModuleBlueprint {
        id
        name
        status {
            __typename
            ... on Archived {
                version
            }
        }
        orderWeight
        groupId
        categoryId
        categoryReference
        moduleType
        events {
            onAttach
            onDelete
            onCreated
            onChange
        }
        xAttachmentPoints {
            id
            position {
                x
                y
                z
            }
            orientation {
                x
                y
                z
            }
            properties
        }
        yAttachmentPoints {
            id
            position {
                x
                y
                z
            }
            orientation {
                x
                y
                z
            }
            properties
        }
        model {
            id
            filename
            mimeType
            directory
            url
        }
        visualXAttachmentPoints {
            position {
                x
                y
                z
            }
            orientation {
                x
                y
                z
            }
            xAttachmentPointIds
        }
        visualYAttachmentPoint {
            isEditButton
            position {
                x
                y
                z
            }
            orientation {
                x
                y
                z
            }
            yAttachmentPointId
        }
        invariants {
            rule
            name
            correction
            id
            translations {
                values {
                    key
                    value
                }
            }
            translationKeys {
                key
                value
            }
            disableDuringOnChange
        }
        invariantTemplates
        eventTemplate
        boundingBoxes {
            size {
                length
                width
                height
            }
            position {
                x
                y
                z
            },
            color {
                color
                id
                overlapAllowed
            }
        }
        considerAsFree
    }
`;

export const VARIANT_FIELDS = gql`
    fragment variantFragment on ModuleVariant {
        id
        blueprintId
        skus
        name
        catalogIcon {
            id
            filename
            mimeType
            directory
            url
        }
        visualChanges {
            __typename
            ... on Invisible {
                name
            }
            ... on MaterialChange {
                changes {
                    values {
                        value
                        key
                    }
                }
            }
            ... on Translation {
                translate {
                    x
                    y
                    z
                }
                translateModule
                xaps
                yaps
            }
        }
        attributeValues {
            values {
                key
                value
            }
        }
        showInCatalog
        translations {
            values  {
                key
                value
            }
        }
        groupId
        featureImage {
            id
            filename
            mimeType
            directory
            url
        }
    }
`;

export const FATVARIANT = gql`
    ${BLUEPRINT_FIELDS}
    ${VARIANT_FIELDS}
    fragment extendedBlueprintFragment on ModuleBlueprint {
        ...blueprintFragment
        variantsPerWoodType {
            values {
                key
                value {
                    ...variantFragment
                }
            }
        }
    }
`;

export const MANUALPAYLOAD = gql`
    fragment configurationManualPayloadFragment on ManualConfiguration {
        configuration {
            id
            name
            email
            code
            configurationPlacement {
                placements {
                    id
                    variantId
                    transformation {
                        r1 {
                            c1, c2, c3, c4
                        }
                        r2 {
                            c1, c2, c3, c4
                        }
                        r3 {
                            c1, c2, c3, c4
                        }
                        r4 {
                            c1, c2, c3, c4
                        }
                    }
                    xAttachments {
                        values {
                            key
                            value {
                                placementId
                                yAttachmentId
                            }
                        }
                    }
                }
                originPlacement
                woodType
            }
            isPreConfiguration
            installationService
            markerPlacement {
                mainMarkers {
                    values {
                        key
                        value
                    }
                }
                visualXAttachmentPointMarkers {
                    values {
                        key
                        value
                    }
                }
                attachedToMarkers {
                    values {
                        key
                        value
                    }
                }
            }
        }
        orderNumber
        images {
            frontView
            rightView
            sideView
            measurements
            sides
        }
        skuPositions {
            values {
                key
                value {
                    placementInfoMap {
                        id
                        markers {
                              marker
                              spacialPosition {
                                  position {
                                      x
                                      y
                                      z
                                  }
                                  orientation {
                                      x
                                      y
                                      z
                                  }
                              }
                            }
                        }
                    purchasePrice
                    }
                }
            }
        ean
        configurationName
        totalPurchasePrice
    }
`;
